import React, { useState } from 'react';

import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';

import ImportableRoomImages from '~/components/Offers/Detail/OfferBuilder/Content/ImagesVideo/OfferGallery/ImportableRoomImages';

interface Props {
  roomId: string | undefined;
  roomName: string;
  existingImageIds: Array<string>;
  isSyncing: boolean;
  onSync: () => void;
  onImport: (imageIds: Array<string>) => Promise<unknown>;
}

export default function RoomImagesSyncImportBar({
  roomId,
  roomName,
  existingImageIds,
  isSyncing,
  onSync,
  onImport,
}: Props) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedImageIds, setSelectedImageIds] = useState<Array<string>>([]);
  const [isImporting, setIsImporting] = useState(false);
  const existingImageIdSet = new Set(existingImageIds);

  function toggleSelected(imageId: string) {
    setSelectedImageIds((current) =>
      current.includes(imageId) ? current.filter((id) => id !== imageId) : current.concat(imageId),
    );
  }

  function closeDialog() {
    setIsDialogOpen(false);
    setSelectedImageIds([]);
  }

  async function handleImport() {
    setIsImporting(true);
    try {
      await onImport(selectedImageIds);
      closeDialog();
    } catch (error) {
      const failedImageIds = error?.failedImageIds;
      if (Array.isArray(failedImageIds)) {
        setSelectedImageIds(failedImageIds);
      }
    } finally {
      setIsImporting(false);
    }
  }

  return (
    <Box>
      <Stack direction="row" gap={2}>
        <Button variant="outlined" onClick={onSync} disabled={isSyncing || !roomId}>
          {isSyncing ? 'Syncing...' : 'Sync Room Images'}
        </Button>
        <Button variant="outlined" onClick={() => setIsDialogOpen(true)} disabled={!roomId || isSyncing}>
          Import Room Images
        </Button>
      </Stack>

      <Dialog open={isDialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>Import accommodation room images</DialogTitle>
        <DialogContent dividers>
          <ImportableRoomImages
            roomId={roomId}
            roomName={roomName}
            open={isDialogOpen}
            existingImageIdSet={existingImageIdSet}
            selectedImageIds={selectedImageIds}
            onToggleSelected={toggleSelected}
            onImagesLoaded={() => {}}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={isImporting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={selectedImageIds.length === 0 || isImporting}
            onClick={handleImport}
            startIcon={isImporting ? <CircularProgress size={16} /> : undefined}
          >
            Import {selectedImageIds.length > 0 ? `(${selectedImageIds.length})` : ''}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
